import { Fragment, useCallback, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'

// Every table row in the console opens to the detail of what it stands for,
// right underneath it. Owner, 2026-10-30: "make each row of each table,
// clickable to show the detail underneath what its representing".
//
// A row keeps its own cells; this adds the click (and Enter / Space from the
// keyboard), aria-expanded, and the full-width detail row below. Clicks that
// land on something interactive inside the row (a button, a link, a checkbox,
// a select, anything marked data-no-row-toggle) do their own job and do not
// open or close the row, so no page has to stopPropagation on every control.

const INTERACTIVE = 'button, a, input, select, textarea, label, summary, [role="switch"], [role="button"], [data-no-row-toggle]'

/** Which row is open on a table: one at a time, clicked again to close. */
export function useOpenRow(): { openId: string | null; toggle: (id: string) => void; isOpen: (id: string) => boolean } {
  const [openId, setOpenId] = useState<string | null>(null)
  const toggle = useCallback((id: string) => setOpenId((cur) => (cur === id ? null : id)), [])
  return { openId, toggle, isOpen: (id: string) => openId === id }
}

export function ExpandableRow({
  open,
  onToggle,
  colSpan,
  detail,
  children,
  className = '',
  testId
}: {
  open: boolean
  onToggle: () => void
  colSpan: number
  /** Rendered only while open. */
  detail: () => ReactNode
  children: ReactNode
  className?: string
  /** The row gets this test id; its detail row gets `${testId}-detail`. */
  testId?: string
}): JSX.Element {
  const onClick = (e: MouseEvent<HTMLTableRowElement>): void => {
    const target = e.target as Element
    const control = target.closest(INTERACTIVE)
    if (control && e.currentTarget.contains(control)) return
    // A drag to select text is not a click on the row.
    if (window.getSelection()?.toString()) return
    onToggle()
  }
  const onKeyDown = (e: KeyboardEvent<HTMLTableRowElement>): void => {
    if (e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onToggle()
    }
  }
  return (
    <Fragment>
      <tr
        className={`cursor-pointer ${open ? 'row-open' : ''} ${className}`}
        onClick={onClick}
        onKeyDown={onKeyDown}
        tabIndex={0}
        aria-expanded={open}
        data-testid={testId}
      >
        {children}
      </tr>
      {open && (
        <tr className="row-detail" data-testid={testId ? `${testId}-detail` : undefined}>
          <td colSpan={colSpan}>
            <div className="px-1 py-2">{detail()}</div>
          </td>
        </tr>
      )}
    </Fragment>
  )
}

export type Fact = [label: string, value: ReactNode]

/**
 * The detail itself: label / value pairs in a grid, then any actions. A value
 * that is null, undefined or '' shows as an em dash, never as blank or zero,
 * so "not reported" and "nothing" stay apart.
 */
export function DetailGrid({ facts, children, wide }: { facts: Array<Fact | false | null | undefined>; children?: ReactNode; wide?: Fact[] }): JSX.Element {
  const show = (v: ReactNode): ReactNode => (v === null || v === undefined || v === '' ? <span className="text-slate-500">—</span> : v)
  return (
    <div className="grid gap-3 text-xs">
      <dl className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
        {facts.filter((f): f is Fact => !!f).map(([k, v]) => (
          <div key={k} className="min-w-0 flex gap-1.5">
            <dt className="text-slate-500 shrink-0">{k}:</dt>
            <dd className="text-slate-200 min-w-0 break-words">{show(v)}</dd>
          </div>
        ))}
      </dl>
      {wide?.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <div className="text-slate-500 mb-0.5">{k}</div>
          <div className="text-slate-200 whitespace-pre-wrap break-words">{show(v)}</div>
        </div>
      ))}
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  )
}
